/* Aurholm — floating "back to the buy area" card on the product page.
 *
 * Appears once the add-to-cart button has scrolled off the top of the viewport,
 * showing the selected variant's image / title / SKU / price. Clicking it scrolls
 * back and focuses the button.
 *
 * Loaded only on product templates (layout/theme.liquid), so there is no
 * page-type guard here — if the add-to-cart button is missing we simply do
 * nothing, which also covers a product page whose buy buttons are hidden.
 *
 * Two rules keep this off the scrolling hot path, because the product page is
 * where customers scroll most:
 *
 *   Visibility  — an IntersectionObserver watches the submit button. The entry
 *                 carries its own boundingClientRect, so deciding to show or
 *                 hide costs no getBoundingClientRect() and no forced layout.
 *                 (The previous version read that rect on every scroll event.)
 *
 *   Content     — synced once on load and then only on Dawn's variantChange
 *                 event, which is the only thing that can change the title,
 *                 price, SKU or image. Nothing runs while scrolling.
 */

(() => {
  const getProductInfo = () => document.querySelector('product-info') || document;

  const getMainSubmitButton = () => getProductInfo().querySelector('button[id^="ProductSubmitButton-"]');

  const clean = (value) => value?.replace(/\s+/g, ' ').trim() || '';

  const getProductTitle = () => clean(getProductInfo().querySelector('.product__title h1')?.textContent);

  const getProductPrice = () => {
    /* Dawn swaps this container's innerHTML on variant change but keeps the
       container itself, so re-querying inside it stays valid. */
    const priceContainer = getProductInfo().querySelector('[id^="price-"]');
    if (!priceContainer) return '';

    const preferredPrice =
      priceContainer.querySelector('.price__sale .price-item--sale') ||
      priceContainer.querySelector('.price__regular .price-item--regular') ||
      priceContainer.querySelector('.price-item');

    return clean(preferredPrice?.textContent);
  };

  const parseJson = (value, fallback = null) => {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('//') ? `${window.location.protocol}${url}` : url;
  };

  const getSelectedVariant = () => {
    const variantJson = getProductInfo().querySelector('variant-selects [data-selected-variant]')?.textContent;
    return parseJson(variantJson, null);
  };

  const getVariantLabel = (variant) => {
    const sku = variant?.sku?.trim();
    const title = variant?.title?.trim();

    if (sku) return sku;
    if (title && title !== 'Default Title') return title;
    return '';
  };

  const getFallbackProductImage = () => {
    const scope = getProductInfo();
    const activeMedia =
      scope.querySelector('.product__media-item.is-active img') ||
      scope.querySelector('.product__media img') ||
      scope.querySelector('.product-media-container img');

    return normalizeImageUrl(activeMedia?.currentSrc || activeMedia?.src || '');
  };

  const getVariantImage = (variant) => {
    const variantImage =
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_media?.src ||
      variant?.featured_image?.src ||
      variant?.featured_image;

    return normalizeImageUrl(variantImage) || getFallbackProductImage();
  };

  const createReturnCard = () => {
    const existing = document.getElementById('AurholmReturnToCart');
    if (existing) return existing;

    const card = document.createElement('button');
    card.id = 'AurholmReturnToCart';
    card.className = 'aurholm-return-cart';
    card.type = 'button';
    card.hidden = true;
    card.innerHTML = `
      <img class="aurholm-return-cart__image" src="" alt="" width="56" height="56" loading="lazy" data-return-to-cart-image hidden>
      <span class="aurholm-return-cart__content">
        <span class="aurholm-return-cart__title" data-return-to-cart-title></span>
        <span class="aurholm-return-cart__variant" data-return-to-cart-variant hidden></span>
        <span class="aurholm-return-cart__price" data-return-to-cart-price></span>
      </span>
      <span class="aurholm-return-cart__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M7 8V7a5 5 0 0 1 10 0v1h2.2l.8 13H4L4.8 8H7Zm2 0h6V7a3 3 0 0 0-6 0v1Zm2 5v3H8v2h3v3h2v-3h3v-2h-3v-3h-2Z"/>
        </svg>
      </span>
    `;

    document.body.appendChild(card);
    return card;
  };

  const syncReturnCard = (card, variant) => {
    const image = card.querySelector('[data-return-to-cart-image]');
    const title = card.querySelector('[data-return-to-cart-title]');
    const variantEl = card.querySelector('[data-return-to-cart-variant]');
    const price = card.querySelector('[data-return-to-cart-price]');
    const imageUrl = getVariantImage(variant);
    const variantLabel = getVariantLabel(variant);
    const productTitle = getProductTitle();

    title.textContent = productTitle;
    price.textContent = getProductPrice();
    variantEl.textContent = variantLabel;
    variantEl.hidden = !variantLabel;

    if (image) {
      image.hidden = !imageUrl;
      /* Guard the assignment: re-setting an identical src costs a wasted decode
         in some browsers, and this now runs on every variant change. */
      if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
      image.alt = imageUrl ? productTitle : '';
    }
  };

  const scrollToPurchaseArea = () => {
    const mainButton = getMainSubmitButton();
    if (!mainButton) return;

    mainButton.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => {
      mainButton.focus({ preventScroll: true });
    }, 450);
  };

  const init = () => {
    const mainButton = getMainSubmitButton();
    if (!mainButton) return;

    const card = createReturnCard();
    card.addEventListener('click', scrollToPurchaseArea);
    syncReturnCard(card, getSelectedVariant());

    /* Show only when the button sits entirely ABOVE the viewport. "Not
       intersecting" also covers "still below the fold" on first paint, which is
       why the rect the entry hands us is checked rather than isIntersecting
       alone. Reading entry.boundingClientRect does not force a layout. */
    new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        card.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
      },
      { threshold: 0 }
    ).observe(mainButton);

    /* Dawn keeps the submit button element across variant changes (it toggles
       `disabled` rather than replacing the node), so the observer above never
       needs re-attaching — only the card's contents do. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        syncReturnCard(card, event?.data?.variant || getSelectedVariant());
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
